import dayjs from "../../../utils/dayjs";
import type { Dayjs } from "dayjs";
import { CLOSUREMSG_DEFAULT_FILTER_ITEMS } from "../../../constants";

export interface RepeatPattern {
  frequency?: string;
  byWeekDay?: string[];
  until?: string;
  interval?: number | string;
  byWeekDayN?: number | string;
  byDay?: number;
  byMonth?: number | string;
  [key: string]: unknown;
}

interface ClosureMsgObj {
  repeatPattern: RepeatPattern;
  isDailyOption2Selected?: boolean;
  isMonthlyOption2Selected?: boolean;
  isYearlyOption2Selected?: boolean;
}

export const closureMsgRepeatPattern = (obj: ClosureMsgObj) => {
  let repeatPattern: RepeatPattern | null = null;
  switch (obj.repeatPattern.frequency) {
    case "DAILY": {
      if (obj.isDailyOption2Selected) {
        repeatPattern = {
          frequency: obj.repeatPattern.frequency,
          byWeekDay: obj.repeatPattern.byWeekDay
            ? obj.repeatPattern.byWeekDay
            : ["MO"],
          until: obj.repeatPattern.until,
        };
      } else {
        repeatPattern = {
          frequency: obj.repeatPattern.frequency,
          interval: obj.repeatPattern.interval
            ? Number(obj.repeatPattern.interval)
            : 1,
          until: obj.repeatPattern.until,
        };
      }
      break;
    }
    case "WEEKLY": {
      repeatPattern = {
        frequency: obj.repeatPattern.frequency,
        interval: obj.repeatPattern.interval
          ? Number(obj.repeatPattern.interval)
          : 1,
        byWeekDay: obj.repeatPattern.byWeekDay
          ? obj.repeatPattern.byWeekDay
          : ["MO"],
        until: obj.repeatPattern.until,
      };
      break;
    }
    case "MONTHLY": {
      if (obj.isMonthlyOption2Selected) {
        repeatPattern = {
          frequency: obj.repeatPattern.frequency,
          byWeekDay: obj.repeatPattern.byWeekDay
            ? obj.repeatPattern.byWeekDay
            : ["MO"],
          interval: obj.repeatPattern.interval
            ? Number(obj.repeatPattern.interval)
            : 1,
          byWeekDayN: obj.repeatPattern.byWeekDayN
            ? obj.repeatPattern.byWeekDayN
            : 1,
          until: obj.repeatPattern.until,
        };
      } else {
        repeatPattern = {
          frequency: obj.repeatPattern.frequency,
          byDay: obj.repeatPattern.byDay ? obj.repeatPattern.byDay : 1,
          interval: obj.repeatPattern.interval
            ? Number(obj.repeatPattern.interval)
            : 1,
          until: obj.repeatPattern.until,
        };
      }
      break;
    }
    case "YEARLY": {
      if (obj.isYearlyOption2Selected) {
        repeatPattern = {
          frequency: obj.repeatPattern.frequency,
          byWeekDay: obj.repeatPattern.byWeekDay
            ? obj.repeatPattern.byWeekDay
            : ["MO"],
          interval: obj.repeatPattern.interval
            ? Number(obj.repeatPattern.interval)
            : 1,
          byWeekDayN: obj.repeatPattern.byWeekDayN
            ? obj.repeatPattern.byWeekDayN
            : 1,
          byMonth: obj.repeatPattern.byMonth ? obj.repeatPattern.byMonth : 1,
          until: obj.repeatPattern.until,
        };
      } else {
        repeatPattern = {
          frequency: obj.repeatPattern.frequency,
          byDay: obj.repeatPattern.byDay ? obj.repeatPattern.byDay : 1,
          byMonth: obj.repeatPattern.byMonth ? obj.repeatPattern.byMonth : 1,
          interval: obj.repeatPattern.interval ? obj.repeatPattern.interval : 1,
          until: obj.repeatPattern.until,
        };
      }
      break;
    }
    default: {
      break;
    }
  }
  return repeatPattern;
};

type ClosureMsgRecord = Record<string, unknown> & {
  status?: string;
  category?: string;
  type?: string;
};

const closureMsgFunnelFilterData = (
  closureMsgData: ClosureMsgRecord[],
  filteredItem: string[],
): ClosureMsgRecord[] => {
  let filterData: ClosureMsgRecord[] = [];
  const isAllSelected = filteredItem?.includes("all");

  const isDefaultFilterItem =
    filteredItem.length === CLOSUREMSG_DEFAULT_FILTER_ITEMS.length &&
    filteredItem.every(
      (element: string, index: number) =>
        element === CLOSUREMSG_DEFAULT_FILTER_ITEMS[index],
    );
  if (isAllSelected) {
    filterData = [...closureMsgData];
  } else if (isDefaultFilterItem) {
    filterData = closureMsgData?.filter((f) => f.status !== "Expired");
  } else {
    filterData = closureMsgData?.filter(
      (f) =>
        filteredItem.includes(f.category as string) &&
        filteredItem.includes(f.type as string) &&
        filteredItem.includes(f.status as string),
    );
  }
  return filterData;
};

type FormattedRecord = ClosureMsgRecord & {
  formattedStartDate?: Dayjs;
  startDate?: string;
  startTime?: string;
  startTimeZone?: string;
  endDate?: string;
};

export const closureMsgFilterItem = (
  tabValue: string,
  closureMsgData: ClosureMsgRecord[],
  filteredItem: string[],
  fromDate?: unknown,
  toDate?: unknown,
) => {
  let funnelFilterData = closureMsgFunnelFilterData(
    closureMsgData,
    filteredItem,
  ) as FormattedRecord[];
  funnelFilterData = addFormattedTimeToFunnelFilterData(funnelFilterData);
  let filterClosureMsgData: FormattedRecord[] | null = null;
  const currentDate = dayjs().startOf("day");
  const after30Days = dayjs().add(30, "days").endOf("day");
  switch (tabValue) {
    case "month": {
      filterClosureMsgData =
        funnelFilterData &&
        funnelFilterData.filter((item) => {
          return currentDate.isSame(item.formattedStartDate, "month");
        });
      break;
    }
    case "30days": {
      filterClosureMsgData =
        funnelFilterData &&
        funnelFilterData.filter((item) => {
          return (
            item.formattedStartDate?.isSameOrAfter(currentDate) &&
            item.formattedStartDate?.isSameOrBefore(after30Days)
          );
        });
      break;
    }
    case "week": {
      filterClosureMsgData =
        funnelFilterData &&
        funnelFilterData.filter((item) => {
          const result =
            currentDate.week() - (item.formattedStartDate?.week() ?? 0);
          return result === 0;
        });
      break;
    }
    case "day": {
      filterClosureMsgData =
        funnelFilterData &&
        funnelFilterData.filter((item) => {
          return (
            currentDate
              .startOf("day")
              .isSameOrAfter(item.startDate as string) &&
            currentDate.startOf("day").isSameOrBefore(item.endDate as string)
          );
        });
      break;
    }
    case "custom": {
      filterClosureMsgData =
        funnelFilterData &&
        funnelFilterData.filter((item) => {
          return (
            item.formattedStartDate?.isSameOrAfter(
              dayjs(fromDate as string)
                .startOf("day")
                .tz(item.startTimeZone as string),
            ) &&
            item.formattedStartDate?.isSameOrBefore(
              dayjs(toDate as string)
                .endOf("day")
                .tz(item.startTimeZone as string),
            )
          );
        });
      break;
    }
    default: {
      break;
    }
  }
  return filterClosureMsgData;
};

const addFormattedTimeToFunnelFilterData = (
  funnelFilterData: FormattedRecord[],
): FormattedRecord[] => {
  return (
    funnelFilterData?.map((data) => {
      const result: FormattedRecord = { ...data };
      if (result?.startDate && result?.startTime && result?.startTimeZone) {
        result.formattedStartDate = dayjs(
          `${result.startDate} ${result.startTime}`,
          "YYYY-MM-DD h:mm A",
        ).tz(result.startTimeZone);
      }
      return result;
    }) ?? []
  );
};

export const isValidDateTime = (dateTimeString: string) => {
  const givenDateTime = dayjs(dateTimeString, "MM/DD/YYYY hh:mm A");
  const currentDateTime = dayjs();
  return givenDateTime.isSameOrAfter(currentDateTime);
};

export const isValidDateTimeWithStartEnd = (
  startDate: string,
  endDate: string,
) => {
  const startDateTime = dayjs(startDate, "MM/DD/YYYY hh:mm A");
  const endDateTime = dayjs(endDate, "MM/DD/YYYY hh:mm A");
  return endDateTime.isSameOrAfter(startDateTime);
};

export const isEndDateTimeValid = (
  startDate: string | Date | undefined,
  endDate: string | Date | undefined,
  startTime: string,
  endTime: string,
  startTimeOption: string,
  endTimeOption: string,
  timeZone = "America/Los_Angeles",
) => {
  // Return false if endDate is undefined
  if (endDate === undefined) {
    return false;
  }

  const format = "YYYYMMDDHHmmss";
  const sDateTime = dayjs(
    `${dayjs(startDate).format("MM/DD/YYYY")} ${startTime}`,
  );

  const eDateTime = dayjs(`${dayjs(endDate).format("MM/DD/YYYY")} ${endTime}`);

  const currentDateTime = dayjs().tz(timeZone).format(format);

  if (
    (startTimeOption === "immediately" ||
      (startTimeOption === "userDefined" &&
        sDateTime &&
        sDateTime.isValid() &&
        eDateTime.format(format) > sDateTime.format(format))) &&
    eDateTime &&
    eDateTime.isValid() &&
    eDateTime.format(format) > currentDateTime
  ) {
    return true;
  }
  return false;
};

export const isStartDateTimeValid = (
  startTime: string,
  startDate: string | Date | undefined,
  endTime: string,
  timeZone = "America/Los_Angeles",
) => {
  // Return false if startDate is undefined
  if (startDate === undefined) {
    return false;
  }

  const format = "YYYYMMDDHHmmss";
  const compareTime = dayjs(endTime, "hh:mm A");
  const newTime = dayjs(startTime, "hh:mm A");
  const cnewTime = newTime.format("hh:mm A");
  const currentDateTime = dayjs().tz(timeZone).format(format);
  const finalSDateTime = dayjs(
    `${dayjs(startDate).format("MM/DD/YYYY")} ${cnewTime}`,
  ).format(format);
  if (
    newTime &&
    newTime.isValid() &&
    compareTime &&
    compareTime.isValid() &&
    finalSDateTime > currentDateTime
  ) {
    return true;
  }
  return false;
};

export const isStartAndEndDateTimeValid = (
  startDate: string | Date | undefined,
  endDate: string | Date | undefined,
  startTime: string,
  endTime: string,
  startTimeOption: string,
  endTimeOption: string,
  timeZone = "America/Los_Angeles",
) => {
  if (startTimeOption === "immediately") {
    timeZone = "America/Los_Angeles";
  }

  const format = "YYYYMMDDHHmmss";
  const finalSDateTime = dayjs(
    `${dayjs(startDate).format("MM/DD/YYYY")} ${startTime}`,
  );
  const finalEDateTime = dayjs(
    `${dayjs(endDate).format("MM/DD/YYYY")} ${endTime}`,
  );
  const currentDateTime = dayjs().tz(timeZone);

  const isSValid =
    finalSDateTime.format(format) > currentDateTime.format(format);
  const isEValid =
    finalEDateTime.format(format) > currentDateTime.format(format);
  return (
    (startTimeOption === "immediately" && endTimeOption === "immediately") ||
    (startTimeOption === "userDefined" &&
      endTimeOption === "immediately" &&
      isSValid) ||
    (startTimeOption === "immediately" &&
      endTimeOption === "userDefined" &&
      isEValid) ||
    (startTimeOption === "userDefined" &&
      endTimeOption === "userDefined" &&
      isSValid &&
      isEValid &&
      finalEDateTime.format(format) > finalSDateTime.format(format))
  );
};

export const sortingCaseInSensitive = <T extends { name: string }>(
  strArray: T[],
): T[] => {
  return strArray?.sort((a, b) =>
    a.name.toLowerCase() > b.name.toLowerCase()
      ? 1
      : b.name.toLowerCase() > a.name.toLowerCase()
        ? -1
        : 0,
  );
};

export function closureRepeatEndDate(): import("dayjs").Dayjs {
  return dayjs().add(1, "days");
}

export function dayjsToISOString(date: string | Date | Dayjs) {
  return dayjs(date).toISOString();
}

// Keep for backward compatibility
export const momentToISOString = dayjsToISOString;

export function getTextLengthErrorMessage(length: number) {
  return `Text length exceeds ${length} characters`;
}

export function getDuplicateNameErrorMessage(name: string) {
  return `${name} already exist. Please try different`;
}

export function getNumberErrorMessage(filedName: string, type: string) {
  return `${filedName} type must be ${type}`;
}
export const isNumeric = (value: string) => {
  return /^[0-9]+$/.test(value);
};
